import { Component, inject, computed, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { DropdownComponent } from '@app/shared/components/dropdown/dropdown.component';

import { SupportedLanguage } from '@app/app.component';
import { SUPPORTED_LANGUAGES, MARKET_CURRENCIES } from '@app/shared/constants/constants';
import { selectAuthUserStore } from '@store/store-auth-user/auth-user.selectors';
import { AuthUserActions } from '@store/store-auth-user/auth-user.actions';
import { AppStore } from '@store/app.store';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

// ==================== TOP BANNER COMPONENT ====================
// Welcome message, currency/language selectors, and logout for the top banner.
@Component({
    selector: 'app-top-banner',
    standalone: true,
    imports: [CommonModule, DropdownComponent, TranslateModule],
    templateUrl: './top-banner.html',
    styleUrl: './top-banner.scss',
})
export class TopBanner {
    private translate = inject(TranslateService);
    private store = inject(Store<AppStore>);

    public currentLanguage = signal<SupportedLanguage>('sv');
    public currentCurrency = signal<string>('SEK');

    public languageOptions = computed(() => SUPPORTED_LANGUAGES);
    public currencyOptions = computed(() => MARKET_CURRENCIES);

    public currentLanguageOption = computed(() => this.languageOptions().find(l => l.languageCode === this.currentLanguage()) ?? null);
    public currentCurrencyOption = computed(() => this.currencyOptions().find(c => c.currencyCode === this.currentCurrency()) ?? null);

    public trackByLanguageCode = (l: any) => l.languageCode;
    public trackByCurrencyCode = (c: any) => c.currencyCode;

    public authUserState = toSignal(this.store.select(selectAuthUserStore), { requireSync: true });
    private langChange = toSignal(this.translate.onLangChange, { requireSync: false });
    public userName = computed(() => this.authUserState().userName ?? '');

    constructor() {
        this.currentLanguage.set((this.translate.currentLang as SupportedLanguage) || 'sv');

        // Restore the user's previously selected currency, if any
        try {
            const savedCurrency = localStorage.getItem('app-currency');
            if (savedCurrency) {
                this.currentCurrency.set(savedCurrency);
            }
        } catch (error) {
            console.warn('[TopBannerComponent] Could not read currency from localStorage:', error);
        }

        // Keep currentLanguage in sync if the language changes elsewhere in the app
        effect(() => {
            const langChangeEvent = this.langChange();
            if (langChangeEvent) {
                this.currentLanguage.set(langChangeEvent.lang as SupportedLanguage);
            }
        });
    }

    public onLanguageSelected(language: { languageCode: string }): void {
        const languageCode = language.languageCode as SupportedLanguage;
        this.translate.use(languageCode).subscribe({
            next: () => {
                this.currentLanguage.set(languageCode);
                try {
                    localStorage.setItem('app-language', languageCode);
                } catch (error) {
                    console.warn('[TopBannerComponent] Could not save language to localStorage:', error);
                }
            },
        });
    }

    public onCurrencySelected(currency: { currencyCode: string }): void {
        const currencyCode = currency.currencyCode;
        this.currentCurrency.set(currencyCode);
        try {
            localStorage.setItem('app-currency', currencyCode);
        } catch (error) {
            console.warn('[TopBannerComponent] Could not save currency to localStorage:', error);
        }
    }

    public logout(): void {
        this.store.dispatch(AuthUserActions.customerLogout());
    }
}
